'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones del servicio de Organización
import {
  fetchAllOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  OrganizationAPI,
  OrganizationData,
} from './organization'; // Asegúrate de la ruta correcta

// Componentes Separados
import OrganizationFormModal from './components/OrganizationFormModal';
import OrganizationList from './components/OrganizationList';
import { getHeaderConfig } from '../Users/Users'; // Reutilizamos la configuración del Header

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: OrganizationData = {
  name: '',
  short_name: '',
  oid: '',
  country_code: '',
  structure_id: '',
  suffix: '',
  status: true, // Por defecto activo
  base64_logo: '',
};

function OrganizationPanel({ servicesManager }) {
  const [organizations, setOrganizations] = useState<OrganizationAPI[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<OrganizationAPI | null>(null);
  const [formData, setFormData] = useState<OrganizationData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  // Configuración del Header
  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  // --- Lógica de Seguridad y Carga de Datos ---
  useEffect(() => {
    // Seguridad: Redirigir si no hay token (reutilizado)
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;
    if (!session?.token) {
      toast.warning('Hubo un error, no tienes permisos para esta página.');
      navigate('/');
    }
  }, [navigate]);

  // Cargar Organizaciones
  const loadOrganizations = async () => {
    try {
      setIsLoading(true);
      const apiOrganizations = await fetchAllOrganizations();
      setOrganizations(apiOrganizations);
    } catch (error) {
      toast.error('Error al obtener organizaciones: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  // --- Lógica de Filtro ---
  const filteredOrganizations = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.oid.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD ---

  const handleCreateOrganization = async () => {
    try {
      const response = await createOrganization(formData);
      toast.success(`Organización ${response.name} creada exitosamente.`);
      await loadOrganizations();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear organización: ' + (err.message || err));
    }
  };

  const handleUpdateOrganization = async () => {
    if (!editingOrganization) return;
    try {
      // Usamos los datos del formulario para la actualización
      const response = await updateOrganization(editingOrganization._id, formData);
      toast.success(`Organización ${response.name} actualizada.`);
      await loadOrganizations();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar organización: ' + (err.message || err));
    }
  };

  const handleDeleteOrganization = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta organización?')) {
      return;
    }
    try {
      await deleteOrganization(id);
      toast.success('Organización eliminada correctamente.');
      setOrganizations((prev) => prev.filter((org) => org._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar la organización: ' + (err.message || err));
    }
  };

  const handleEditOrganization = (organization: OrganizationAPI) => {
    setEditingOrganization(organization);
    // Mapear OrganizationAPI a OrganizationData
    setFormData({
      name: organization.name,
      short_name: organization.short_name,
      oid: organization.oid,
      country_code: organization.country_code,
      structure_id: organization.structure_id,
      suffix: organization.suffix,
      status: organization.status,
      base64_logo: organization.base64_logo || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingOrganization(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingOrganization ? handleUpdateOrganization() : handleCreateOrganization();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Organizaciones</h1>
            <p className="mt-1">Administra las entidades del sistema (Hospitales, Clínicas)</p>
          </div>
          <button
            onClick={() => {
              setEditingOrganization(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Organización
          </button>
        </div>

        {/* Lista de Organizaciones */}
        <OrganizationList
          organizations={filteredOrganizations}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditOrganization={handleEditOrganization}
          handleDeleteOrganization={handleDeleteOrganization}
        />
      </div>

      {/* Modal de Formulario */}
      <OrganizationFormModal
        isDialogOpen={isDialogOpen}
        editingOrganization={editingOrganization}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />
    </div>
  );
}

OrganizationPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default OrganizationPanel;
